import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getColors, fonts } from '../theme';
import { TIERS } from '../config/tiers';
import { isNativePlatform, isIOS } from '../utils/platform';

const SPECIMEN = [
  { time: '07:00', name: 'Morning pages', done: true },
  { time: '07:30', name: 'Run 5k', done: false },
  { time: '21:00', name: 'Read 30 minutes', done: false },
];

const STEPS = [
  { n: '01', mark: '•', title: 'Write it down.', text: "Add the habits you're building, the tasks in front of you, and the goals behind them." },
  { n: '02', mark: '×', title: 'Tick it off.', text: 'Squares fill, tasks get crossed out, streaks count up. Nothing else competes for your attention.' },
  { n: '03', mark: '–', title: 'Read the score.', text: 'One number from 0 to 100, weighted by what you actually track.' },
];

const CONTENTS = [
  { name: 'Habits', desc: 'daily squares, streaks, the month at a glance' },
  { name: 'Tasks', desc: 'grouped by when, sorted by priority' },
  { name: 'Goals', desc: 'milestones, SMART details, linked habits' },
  { name: 'Calendar', desc: 'two-way Google Calendar sync' },
  { name: 'Focus timer', desc: '25-minute Pomodoro sessions', to: '/pomodoro-technique' },
  { name: 'Productivity score', desc: 'one number, fully explained', to: '/how-scoring-works' },
];

const PRO_FEATURES = [
  'Unlimited habits, tasks, goals, and events',
  'Goal milestones',
  'Two-way Google Calendar sync',
  'Productivity score and analytics',
  'Recurring tasks',
  'Export your data',
  'Priority support',
];

const LandingPage = ({ isDark, onGetStarted }) => {
  const colors = getColors(isDark);
  const isNativeIOS = isNativePlatform() && isIOS();
  const [menuOpen, setMenuOpen] = useState(false);
  const [billing, setBilling] = useState('yearly');
  const [specimen, setSpecimen] = useState(SPECIMEN);

  const free = TIERS.free;
  const pro = TIERS.pro;
  const specimenScore = Math.round((100 * specimen.filter(h => h.done).length) / specimen.length);
  const ink = colors.textPrimary;
  const rule = `1px solid ${colors.border}`;
  const inkRule = `1px solid ${ink}`;

  const Square = ({ on }) => (
    <span
      aria-hidden="true"
      className="inline-block w-2.5 h-2.5 flex-shrink-0"
      style={{ border: `1px solid ${on ? ink : colors.borderStrong}`, backgroundColor: on ? ink : 'transparent' }}
    />
  );

  const SectionHead = ({ title, aside }) => (
    <div className="flex justify-between items-baseline gap-4 pb-2" style={{ borderBottom: inkRule }}>
      <h2 className="text-[15px] font-semibold tracking-tight" style={{ color: ink }}>{title}</h2>
      {aside}
    </div>
  );

  const RuledRow = ({ margin, mark, children }) => (
    <div
      className="grid grid-cols-[50px_24px_minmax(0,1fr)] sm:grid-cols-[64px_28px_minmax(0,1fr)] gap-x-2.5 sm:gap-x-3.5 items-center min-h-[48px]"
      style={{ borderBottom: rule }}
    >
      <span className="self-stretch flex items-center font-mono text-xs" style={{ borderRight: rule, color: colors.textSecondary }}>{margin}</span>
      <span className="flex justify-center font-mono" style={{ color: ink }}>{mark}</span>
      <span className="py-3" style={{ color: ink }}>{children}</span>
    </div>
  );

  const scrollToPlans = () => {
    setMenuOpen(false);
    document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinkClass = 'text-sm transition-colors hover:underline underline-offset-4';

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg, color: ink }}>
      <div className="max-w-[880px] mx-auto px-5">
        {/* Site header */}
        <header
          className="sticky z-30 grid grid-cols-[auto_1fr_auto] items-end gap-6"
          style={{ top: 0, backgroundColor: colors.bg, borderBottom: inkRule, paddingTop: isNativeIOS ? 'max(env(safe-area-inset-top), 44px)' : 0 }}
        >
          <span className="text-[22px] leading-none pt-4 pb-3.5" style={{ fontFamily: fonts.serif }}>mind.</span>
          <nav className="hidden md:flex gap-7 pb-3.5" aria-label="Site">
            <Link to="/about" className={navLinkClass} style={{ color: colors.textSecondary }}>About</Link>
            <button onClick={scrollToPlans} className={navLinkClass} style={{ color: colors.textSecondary }}>Pricing</button>
            <Link to="/how-scoring-works" className={navLinkClass} style={{ color: colors.textSecondary }}>How scoring works</Link>
          </nav>
          <div className="col-start-3 flex items-center gap-4 pb-2.5">
            <button onClick={onGetStarted} className="text-sm" style={{ color: colors.textSecondary }}>
              {isNativeIOS ? 'Get started' : 'Sign in'}
            </button>
            <button
              onClick={onGetStarted}
              className="hidden md:inline-block text-[13px] px-3 py-1.5 transition-opacity hover:opacity-85"
              style={{ backgroundColor: colors.accent, color: colors.onAccent }}
            >
              Start free
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden text-sm px-3 py-1.5"
              style={{
                border: `1px solid ${menuOpen ? colors.accent : colors.borderStrong}`,
                backgroundColor: menuOpen ? colors.accent : 'transparent',
                color: menuOpen ? colors.onAccent : ink,
              }}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
            >
              Menu
            </button>
          </div>
        </header>

        {menuOpen && (
          <div id="site-menu" className="md:hidden" style={{ borderBottom: inkRule }}>
            {[
              { label: 'About', to: '/about' },
              { label: 'Pricing', onClick: scrollToPlans },
              { label: 'How scoring works', to: '/how-scoring-works' },
              { label: 'Pomodoro technique', to: '/pomodoro-technique' },
              { label: 'Install the app', to: '/pwa-info' },
            ].map(item => {
              const cls = 'flex justify-between items-center w-full min-h-[54px] text-base text-left';
              const content = <><span>{item.label}</span><span className="font-mono" style={{ color: colors.textSecondary }}>→</span></>;
              return item.to
                ? <Link key={item.label} to={item.to} className={cls} style={{ borderBottom: rule, color: ink }}>{content}</Link>
                : <button key={item.label} onClick={item.onClick} className={cls} style={{ borderBottom: rule, color: ink }}>{content}</button>;
            })}
            <div className="py-3.5">
              <button onClick={onGetStarted} className="w-full py-3 text-[15px]" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
                Start free
              </button>
            </div>
          </div>
        )}

        {/* Hero */}
        <section className="grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,.85fr)] gap-8 md:gap-14 items-end pt-9 md:pt-14 pb-10" style={{ borderBottom: rule }}>
          <div>
            <p className="font-mono text-xs mb-2.5" style={{ color: colors.textSecondary }}>Habits · Tasks · Goals · Calendar</p>
            <h1 className="text-[40px] md:text-[66px] font-semibold leading-[.98] max-w-[13ch]" style={{ letterSpacing: '-0.045em' }}>
              Habits, tasks, and goals on one quiet page.
            </h1>
            <p className="text-lg leading-relaxed mt-5 max-w-[58ch]" style={{ color: colors.textSecondary }}>
              mind. is a minimalist habit tracker. Tick off habits, cross out tasks, and let one number, your productivity score, tell you how the week is really going.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-7">
              <button
                onClick={onGetStarted}
                className="px-5 py-3 text-[15px] transition-opacity hover:opacity-85"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                Start free
              </button>
              <Link to="/how-scoring-works" className="text-sm underline underline-offset-4">How the score works</Link>
            </div>
            <p className="font-mono text-xs mt-3" style={{ color: colors.textSecondary }}>
              Free for up to {free.limits.maxHabits} habits. No card needed.
            </p>
          </div>

          {/* Try it: a live page from the notebook */}
          <div style={{ border: inkRule }} aria-label="Try it: tick the habits">
            <div className="flex justify-between font-mono text-[11px] px-3.5 py-2.5" style={{ borderBottom: inkRule, color: colors.textSecondary }}>
              <span>Try it</span>
              <span>{new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            </div>
            {specimen.map((h, i) => (
              <button
                key={h.name}
                onClick={() => setSpecimen(prev => prev.map((x, j) => (j === i ? { ...x, done: !x.done } : x)))}
                className="grid grid-cols-[52px_28px_minmax(0,1fr)] gap-x-3 items-center w-full min-h-[48px] pr-3.5 text-left notebook-row-hover"
                style={{ borderBottom: rule }}
                aria-pressed={h.done}
              >
                <span className="self-stretch flex items-center pl-3.5 font-mono text-xs" style={{ borderRight: rule, color: colors.textSecondary }}>{h.time}</span>
                <span className="grid place-items-center w-[18px] h-[18px] justify-self-center transition-colors" style={{ border: `1.5px solid ${ink}`, backgroundColor: h.done ? ink : 'transparent' }}>
                  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                    <path d="M3.5 8.4 6.6 11.4 12.5 4.8" pathLength="1" fill="none" stroke={colors.onAccent} strokeWidth="2" strokeLinecap="square" className={`ink-check ${h.done ? 'on' : ''}`} />
                  </svg>
                </span>
                <span className="text-[15px]">{h.name}</span>
              </button>
            ))}
            <div className="flex items-baseline gap-3 p-3.5">
              <span className="font-mono font-light text-[44px] leading-[.9] tabular-nums" style={{ letterSpacing: '-0.04em' }}>{specimenScore}</span>
              <span className="text-[13px]" style={{ color: colors.textSecondary }}>today's habit score</span>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="mt-12">
          <SectionHead title="How it works" aside={<span className="font-mono text-xs" style={{ color: colors.textSecondary }}>3 steps</span>} />
          {STEPS.map(step => (
            <RuledRow key={step.n} margin={step.n} mark={step.mark}>
              <b className="font-semibold">{step.title}</b> {step.text}
            </RuledRow>
          ))}
        </section>

        {/* Contents */}
        <section className="mt-12">
          <SectionHead title="Contents" aside={<span className="font-mono text-xs" style={{ color: colors.textSecondary }}>what's inside</span>} />
          {CONTENTS.map(item => {
            const inner = (
              <>
                <span className="text-base">{item.name}</span>
                <span className="hidden sm:block flex-1 min-w-6 -translate-y-1" style={{ borderBottom: `1px dotted ${colors.borderStrong}` }} />
                <span className="basis-full sm:basis-auto text-sm" style={{ color: colors.textSecondary }}>{item.desc}</span>
                {item.to && <span className="font-mono text-xs">→</span>}
              </>
            );
            const cls = 'flex flex-wrap sm:flex-nowrap items-baseline gap-x-2.5 gap-y-0.5 py-3.5 w-full';
            return item.to
              ? <Link key={item.name} to={item.to} className={`${cls} hover:underline underline-offset-4`} style={{ borderBottom: rule, color: ink }}>{inner}</Link>
              : <div key={item.name} className={cls} style={{ borderBottom: rule }}>{inner}</div>;
          })}
        </section>

        {/* Plans */}
        <section id="plans" className="mt-12 scroll-mt-20">
          <SectionHead
            title="Plans"
            aside={
              <div className="inline-flex font-mono text-[11px]" style={{ border: `1px solid ${colors.borderStrong}` }} role="group" aria-label="Billing period">
                {['monthly', 'yearly'].map(period => (
                  <button
                    key={period}
                    onClick={() => setBilling(period)}
                    aria-pressed={billing === period}
                    className="px-2.5 py-1"
                    style={{ backgroundColor: billing === period ? colors.accent : 'transparent', color: billing === period ? colors.onAccent : colors.textSecondary }}
                  >
                    {period === 'yearly' ? 'Yearly · save 17%' : 'Monthly'}
                  </button>
                ))}
              </div>
            }
          />
          <div className="grid md:grid-cols-2 gap-9 md:gap-8 mt-6">
            <div className="flex flex-col pt-4" style={{ borderTop: inkRule }}>
              <h3 className="text-xl font-semibold tracking-tight">{free.name}</h3>
              <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{free.description}</p>
              <p className="flex items-baseline gap-2 mt-5 font-mono">
                <span className="text-5xl font-light" style={{ letterSpacing: '-0.04em' }}>$0</span>
                <span className="text-[13px]" style={{ color: colors.textSecondary }}>forever</span>
              </p>
              <ul className="grid gap-2.5 my-6 text-sm flex-1">
                {free.featureList.map(f => (
                  <li key={f.text} className="flex items-center gap-2.5" style={{ color: f.included ? ink : colors.textSecondary }}>
                    <Square on={f.included} />{f.text === 'Custom color themes' ? 'Paper and chalkboard themes' : f.text}
                  </li>
                ))}
              </ul>
              <button onClick={onGetStarted} className="self-start px-4 py-2.5 text-sm" style={{ border: inkRule }}>Start free</button>
            </div>
            <div className="flex flex-col pt-4" style={{ borderTop: inkRule }}>
              <h3 className="text-xl font-semibold tracking-tight">{pro.name}</h3>
              <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>{pro.description}</p>
              <p className="flex items-baseline gap-2 mt-5 font-mono">
                <span className="text-5xl font-light" style={{ letterSpacing: '-0.04em' }}>${billing === 'yearly' ? pro.yearlyPrice : pro.monthlyPrice}</span>
                <span className="text-[13px]" style={{ color: colors.textSecondary }}>{billing === 'yearly' ? 'per year' : 'per month'}</span>
              </p>
              <p className="font-mono text-xs mt-1" style={{ color: colors.textSecondary }}>
                {billing === 'yearly'
                  ? `$${(pro.yearlyPrice / 12).toFixed(2)} a month · save $${pro.monthlyPrice * 12 - pro.yearlyPrice} a year`
                  : `or $${pro.yearlyPrice} a year`}
              </p>
              <ul className="grid gap-2.5 my-6 text-sm flex-1">
                {PRO_FEATURES.map(f => <li key={f} className="flex items-center gap-2.5"><Square on />{f}</li>)}
              </ul>
              <button onClick={onGetStarted} className="self-start px-4 py-2.5 text-sm transition-opacity hover:opacity-85" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
                Start free, upgrade anytime
              </button>
            </div>
          </div>
        </section>

        {/* Privacy */}
        <section className="mt-12">
          <SectionHead title="Your notebook is yours" />
          <p className="mt-4 max-w-[62ch] leading-relaxed" style={{ color: colors.textSecondary }}>
            Secure sign-in and encrypted storage. Your habits, goals, and personal information are never shared with third parties.
          </p>
        </section>

        {/* Final call to action */}
        <section className="mt-12">
          <SectionHead title="Start today's page" />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-5">
            <button onClick={onGetStarted} className="px-5 py-3 text-[15px] transition-opacity hover:opacity-85" style={{ backgroundColor: colors.accent, color: colors.onAccent }}>
              Start free
            </button>
            <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>Pro is ${pro.monthlyPrice} a month when you want more.</span>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="mt-24 pt-6 grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr] gap-7"
          style={{ borderTop: inkRule, paddingBottom: isNativeIOS ? 'max(env(safe-area-inset-bottom), 32px)' : '32px' }}
        >
          <div className="col-span-2 md:col-span-1">
            <span className="text-[22px]" style={{ fontFamily: fonts.serif }}>mind.</span>
            <p className="text-xs mt-2" style={{ color: colors.textSecondary }}>A quiet notebook for habits, tasks, and goals.</p>
          </div>
          <nav aria-label="Product" className="grid gap-2 content-start text-sm">
            <h3 className="font-mono text-[11px] font-normal mb-1" style={{ color: colors.textSecondary }}>Product</h3>
            <button onClick={scrollToPlans} className="text-left hover:underline underline-offset-4">Pricing</button>
            <Link to="/how-scoring-works" className="hover:underline underline-offset-4">How scoring works</Link>
            <Link to="/pomodoro-technique" className="hover:underline underline-offset-4">Pomodoro technique</Link>
            <Link to="/pwa-info" className="hover:underline underline-offset-4">Install the app</Link>
          </nav>
          <nav aria-label="Company" className="grid gap-2 content-start text-sm">
            <h3 className="font-mono text-[11px] font-normal mb-1" style={{ color: colors.textSecondary }}>Company</h3>
            <Link to="/about" className="hover:underline underline-offset-4">About</Link>
            <Link to="/privacy" className="hover:underline underline-offset-4">Privacy</Link>
            <Link to="/terms" className="hover:underline underline-offset-4">Terms</Link>
          </nav>
          <p className="col-span-2 md:col-span-3 font-mono text-xs mt-4" style={{ color: colors.textSecondary }}>
            © {new Date().getFullYear()} Mill Creek Media LLC
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
